import { GetterTree } from "vuex/types";
import { RootState } from "@/store/types";
import { FaqState } from "./types";
import _ from "lodash";

/**
 * Getters for the FAQ module.
 * Provides computed access to FAQ configuration and training data.
 */
const getters: GetterTree<FaqState, RootState> = {
  /**
   * Checks if Rasa NLU is enabled for FAQ.
   *
   * @param {FaqState} state - Module state
   * @param {Object} getters - Other getters
   * @param {RootState} rootState - Root state
   * @returns {boolean} True if Rasa is enabled
   */
  rasaEnabled(state, getters, rootState) {
    return _.get(rootState, "modules.faq.Rasa.enabled", false);
  },

  /**
   * Checks if Elsa NLU is enabled for FAQ.
   *
   * @param {FaqState} state - Module state
   * @param {Object} getters - Other getters
   * @param {RootState} rootState - Root state
   * @returns {boolean} True if Elsa is enabled
   */
  elsaEnabled(state, getters, rootState) {
    return _.get(rootState, "modules.faq.Elsa.enabled", false);
  },

  /**
   * Checks if OpenAI example generation is enabled.
   *
   * @param {FaqState} state - Module state
   * @param {Object} getters - Other getters
   * @param {RootState} rootState - Root state
   * @returns {boolean} True if OpenAI example generation is enabled
   */
  openAIGenerateExamplesEnabled(state, getters, rootState) {
    return (
      _.get(rootState, "modules.pretrained_skills.OpenAI.enabled", false) &&
      _.get(rootState, "modules.pretrained_skills.OpenAI.exampleGeneration.enabled", false)
    );
  },

  /**
   * Returns all available FAQ topics.
   *
   * @param {FaqState} state - Module state
   * @returns {Array<string>} Array of topic names
   */
  getFaqTopics(state, getters) {
    return _.get(state, "topics");
  },

  /**
   * Returns the training in progress flag.
   *
   * @param {FaqState} state - Module state
   * @returns {boolean} True if training is in progress
   */
  getTrainingInProgress(state) {
    return _.get(state, "trainingInProgress");
  },

  /**
   * Returns the publishing in progress flag.
   *
   * @param {FaqState} state - Module state
   * @returns {boolean} True if publishing is in progress
   */
  getPublishingInProgress(state) {
    return _.get(state, "publishingInProgress");
  },

  /**
   * Returns the selected topics for training.
   *
   * @param {FaqState} state - Module state
   * @returns {Array<string>} Array of selected topic names
   */
  getSelectedTopics(state) {
    return _.get(state, "selectedTopics");
  },

  /**
   * Returns the user's departments in lowercase.
   *
   * @param {FaqState} state - Module state
   * @param {Object} getters - Other getters
   * @param {RootState} rootState - Root state
   * @returns {Array<string>} Array of lowercase department names
   */
  userDepartment(state, getters, rootState) {
    const userDepartment = _.get(
      rootState,
      "profile['https://login.keyreply.com/appmetadata'].departments",
      ["general"]
    );

    const uniqueUserDepartment: string[] = _.uniq(userDepartment);
    const lowerCaseUserDepartment = uniqueUserDepartment.map((word) => word.toLowerCase());

    return lowerCaseUserDepartment;
  },

  /**
   * Checks if the evaluation module is enabled.
   *
   * @param {FaqState} state - Module state
   * @param {Object} getters - Other getters
   * @param {RootState} rootState - Root state
   * @returns {boolean} True if evaluation module is enabled
   */
  evaluationModuleEnabled(state, getters, rootState) {
    return _.get(rootState, "modules.evaluation.enabled", false);
  },

  /**
   * Checks if group by topic feature is enabled.
   *
   * @param {FaqState} state - Module state
   * @param {Object} getters - Other getters
   * @param {RootState} rootState - Root state
   * @returns {boolean} True if grouping by topic is enabled
   */
  groupByTopicIsEnabled(state, getters, rootState) {
    return _.get(rootState, "modules.faq.groupByTopic", false);
  },

  /**
   * Returns the conflict difference threshold for FAQ validation.
   *
   * @param {FaqState} state - Module state
   * @param {Object} getters - Other getters
   * @param {RootState} rootState - Root state
   * @returns {number} Threshold value (default 0.5)
   */
  getConflictDifferenceThreshold(state, getters, rootState) {
    return _.get(rootState, "modules.faq.showConflictDifferenceThreshold", 0.5);
  },

  /**
   * Returns current training data based on search state.
   * Returns searched data if backend search is active, otherwise all data.
   *
   * @param {FaqState} state - Module state
   * @param {Object} getters - Other getters
   * @param {RootState} rootState - Root state
   * @returns {Array} Training data array
   */
  currentTrainingData(state, getters, rootState) {
    const rawTrainingData = _.get(rootState, "training.all", []);
    const searchedTrainingData = _.get(rootState, "training.searched", []);

    const backendSearch = _.get(state, "backendSearch", "");
    if (backendSearch) {
      return searchedTrainingData;
    } else {
      return rawTrainingData;
    }
  },

  /**
   * Returns non-archived intents grouped and counted by prediction.
   * Sorted by count in descending order.
   *
   * @param {FaqState} state - Module state
   * @param {Object} getters - Other getters
   * @param {RootState} rootState - Root state
   * @returns {Array<[string, number]>} Array of [intent name, count] tuples
   */
  intents(state, getters, rootState) {
    const intents = _.chain(getters.currentTrainingData)
      .filter((intent) => intent.archived == false)
      .countBy((intent) => intent.prediction)
      .toPairs()
      .orderBy([(intent) => intent[1]], ["desc"])
      .value();

    return intents || [];
  },

  /**
   * Returns the configured file type for training data export.
   *
   * @param {FaqState} state - Module state
   * @param {Object} getters - Other getters
   * @param {RootState} rootState - Root state
   * @returns {string} File type (default 'XLSX')
   */
  getTrainingDownloadFileType(state, getters, rootState) {
    return _.get(rootState, "modules.faq.trainingDownloadFileType", "XLSX");
  },
};

export default getters;
